import type { components } from '../../api/generated/schema';
import type { CursorPage, Page } from '../../domain/common';
import type {
  Listing,
  ListingChanges,
  ListingStatusAction,
  ManifestEntry,
  NewListing,
  PendingPhoto,
  Photo,
} from '../../domain/listings';
import { queryParam, toCollectionRef, toPersonRef } from '../mapping';

type S = components['schemas'];

export function toPhoto(p: S['Photo']): Photo {
  return { id: p.id, url: p.url, position: p.position, width: p.width, height: p.height };
}

export function toPhotos(photos: S['Photo'][]): Photo[] {
  return photos.map(toPhoto).sort((a, b) => a.position - b.position);
}

export function toListing(l: S['Listing']): Listing {
  return {
    id: l.id,
    collection: toCollectionRef(l.collection),
    author: toPersonRef(l.author),
    status: l.status,
    publishState: l.publish_state ?? null,
    publishAt: l.publish_at ?? null,
    announcedAt: l.announced_at,
    title: l.title,
    description: l.description,
    listingType: l.listing_type,
    price: l.price,
    location: l.location,
    expiresAt: l.expires_at,
    isExpired: l.is_expired,
    photos: toPhotos(l.photos),
    commentCount: l.comment_count,
    createdAt: l.created_at,
    updatedAt: l.updated_at,
  };
}

export function toListingPage(page: S['PaginatedListingList']): Page<Listing> {
  const next = queryParam(page.next, 'page');
  return {
    items: page.results.map(toListing),
    hasNext: !!page.next,
    nextPage: next ? Number(next) : null,
  };
}

/** The feed's opaque cursor, taken from its `next` link so screens never see a URL. */
export function cursorFrom(next: string | null | undefined): string | null {
  return queryParam(next, 'cursor');
}

export function toCursorPage(page: S['ListingCursorPage']): CursorPage<Listing> {
  return { items: page.results.map(toListing), nextCursor: cursorFrom(page.next) };
}

export function toPendingPhoto(p: S['PendingImage']): PendingPhoto {
  return {
    id: p.id,
    url: p.url,
    originalFilename: p.original_filename,
    width: p.width,
    height: p.height,
    expiresAt: p.expires_at,
  };
}

export function toManifestEntry(e: S['ListingManifestEntry']): ManifestEntry {
  return { id: e.id, collectionId: e.collection_id, updatedAt: e.updated_at };
}

export function toNewListing(input: NewListing): S['ListingCreateRequest'] {
  const body: S['ListingCreateRequest'] = {
    title: input.title,
    description: input.description,
    listing_type: input.listingType,
    price: input.price,
    location: input.location,
    expires_at: input.expiresAt,
    publish: input.publish,
    publish_at: input.publishAt,
    pending_image_ids: input.pendingPhotoIds,
  };
  // Left out, the server posts to my default collection.
  return input.collectionId ? { collection_id: input.collectionId, ...body } : body;
}

export function toListingChanges(changes: ListingChanges): S['PatchedListingContentUpdateRequest'] {
  const body: S['PatchedListingContentUpdateRequest'] = {};
  if (changes.title !== undefined) body.title = changes.title;
  if (changes.description !== undefined) body.description = changes.description;
  if (changes.listingType !== undefined) body.listing_type = changes.listingType;
  if (changes.price !== undefined) body.price = changes.price;
  if (changes.location !== undefined) body.location = changes.location;
  if (changes.expiresAt !== undefined) body.expires_at = changes.expiresAt;
  return body;
}

const ACTIONS: Record<ListingStatusAction, S['ActionEnum']> = {
  publishNow: 'publish_now',
  schedule: 'schedule',
  unpublish: 'unpublish',
  markSold: 'mark_sold',
  markForSale: 'mark_for_sale',
  withdraw: 'withdraw',
  republish: 'republish',
};

export function toStatusAction(action: ListingStatusAction): S['ActionEnum'] {
  return ACTIONS[action];
}
